/**
 * Parsing des GetCapabilities WMS du SMC.
 *
 * Le serveur retourne un XML; on l'analyse pour extraire :
 *   - dateo : date/heure d'origine de la prévision (Date)
 *   - datef : date/heure de fin disponible (Date)
 *   - deltaHours : pas de temps en heures (number)
 */

const WMS_BASE_URL = 'https://geo.weather.gc.ca/geomet?SERVICE=WMS&VERSION=1.3.0';

/**
 * Télécharge et analyse les capabilities WMS pour une couche donnée.
 *
 * @param {string} layerName - ex: 'HRDPS.CONTINENTAL_TT'
 * @param {AbortSignal} [signal]
 * @returns {Promise<{dateo: Date, datef: Date, deltaHours: number}>}
 */
async function fetchWithRetry(url, fetchOpts, maxRetries = 3) {
  let delay = 2000;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const resp = await fetch(url, fetchOpts);
    if (resp.status !== 429 || attempt === maxRetries) return resp;
    await new Promise(resolve => setTimeout(resolve, delay));
    delay *= 2;
  }
}

export async function fetchCapabilities(layerName, signal) {
  const url = `${WMS_BASE_URL}&REQUEST=GetCapabilities&LAYERS=${layerName}`;
  console.log(url)
  const resp = await fetchWithRetry(url, signal ? { signal } : {});
  if (!resp.ok) throw new Error(`GetCapabilities HTTP ${resp.status} pour ${layerName}`);
  const text = await resp.text();
  return parseCapabilities(text);
}

/**
 * Extrait dateo, datef, deltaHours et la liste complète des timestamps depuis
 * le XML GetCapabilities du SMC.
 *
 * Deux formats possibles pour <Dimension name="time"> :
 *   Format intervalle (HRDPS, RDPS) :
 *     "2024-01-01T00:00:00Z/2024-01-03T00:00:00Z/PT1H"
 *     → la liste est générée par expansion (dateo, dateo+1h, ..., datef).
 *   Format liste (GDPS) — intervalles non uniformes énumérés explicitement :
 *     "2024-01-01T00:00:00Z,2024-01-01T01:00:00Z,...,2024-01-06T00:00:00Z,2024-01-06T03:00:00Z,..."
 *
 * @param {string} xmlText - texte XML brut
 * @returns {{dateo: Date, datef: Date, deltaHours: number, times: Date[]}}
 * @throws {Error} si la dimension temporelle est introuvable ou mal formée
 */
export function parseCapabilities(xmlText) {
  const dimMatch = xmlText.match(/<Dimension name="time"[^>]*>([^<]+)<\/Dimension>/);
  if (!dimMatch) {
    throw new Error('Dimension temporelle introuvable dans les capabilities WMS');
  }
  const content = dimMatch[1];

  // Format intervalle : un ou plusieurs "dateo/datef/intervalle" séparés par des espaces
  // (HRDPS, RDPS — et certains modèles avec plusieurs runs dans le même élément)
  const rangeRe = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)\/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)\/(PT?\d+H|P\d+D)/g;
  let best = null;
  let rm;
  while ((rm = rangeRe.exec(content)) !== null) {
    const dateo = new Date(rm[1]);
    const datef = new Date(rm[2]);
    const deltaHours = parseInterval(rm[3]);
    if (!best || dateo > best.dateo) {
      best = { dateo, datef, deltaHours };
    }
  }
  if (best) {
    const { dateo, datef, deltaHours } = best;
    const stepMs = deltaHours * 3_600_000;
    const times = [];
    for (let t = dateo.getTime(); t <= datef.getTime(); t += stepMs) {
      times.push(new Date(t));
    }
    return { dateo, datef, deltaHours, times };
  }

  // Format liste : "t0,t1,t2,..." (GDPS)
  const timestamps = content.trim().split(',').map(s => new Date(s.trim()));
  if (timestamps.length < 2 || isNaN(timestamps[0])) {
    throw new Error('Format de dimension temporelle non reconnu : ' + content.trim().slice(0, 80));
  }
  let minMs = Infinity;
  for (let i = 1; i < timestamps.length; i++) {
    const delta = timestamps[i] - timestamps[i - 1];
    if (delta > 0 && delta < minMs) minMs = delta;
  }
  return {
    dateo:      timestamps[0],
    datef:      timestamps[timestamps.length - 1],
    deltaHours: minMs / 3_600_000,
    times:      timestamps,
  };
}

/**
 * Convertit un intervalle ISO 8601 simplifié en heures.
 * Supporte : PT1H, PT3H, PT6H, PT12H, PT24H, P1D
 *
 * @param {string} interval - ex: 'PT3H', 'P1D'
 * @returns {number} heures
 * @throws {Error} si le format n'est pas reconnu
 */
export function parseInterval(interval) {
  let m;
  // PTnH → n heures
  m = interval.match(/^PT(\d+)H$/);
  if (m) return parseInt(m[1], 10);

  // PnD → n * 24 heures
  m = interval.match(/^P(\d+)D$/);
  if (m) return parseInt(m[1], 10) * 24;

  throw new Error(`Format d'intervalle non supporté : "${interval}"`);
}
